import { useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { BUSINESS_ANALYST_ROLE } from '../constants/constant';
import { WORKSPACE_OWNER } from '../constants/Constantsi18';
import { resetNavigationToHome } from '../actions/app.action';

export const useProfileNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginData = useSelector((state: any) => state.loginResource);
  
  // Use refs to prevent infinite loops
  const isNavigatingRef = useRef(false);
  const lastProfileRef = useRef<string | null>(null);
  const lastPathRef = useRef<string | null>(null);

  // Memoize screen arrays to prevent unnecessary re-renders
  const businessAnalystScreens = useMemo(() => [
    '/chat', '/', '/artifacts', '/studio', '/businessInsights', '/documentQuery', '/transcriptAnalyzer', '/aiDashboards', '/collaboration', '/feedback'
  ], []);
  
  const workspaceOwnerScreens = useMemo(() => [
    '/', '/aiAgents', '/insightAgent', '/aiDashboards', '/apis', '/monitoringDashboard', '/monitoringLogs', '/tools', '/knowledge', '/domainStores', '/businessGlossary', '/integration'
  ], []);

  useEffect(() => {
    // Prevent navigation if already navigating
    if (isNavigatingRef.current) return;
    
    const currentPath = location.pathname;
    const currentProfile = loginData?.preferredProfile;
    
    // Skip if no meaningful change
    if (lastProfileRef.current === currentProfile && lastPathRef.current === currentPath) {
      return;
    }
    
    if (currentPath && currentProfile && loginData?.profileData?.length > 0) {
      const profile = loginData.profileData.find((item: any) => item.id === currentProfile);
      
      if (!profile || !profile.name) return;

      // const isBusinessAnalyst = profile.name.toLowerCase() === BUSINESS_ANALYST_ROLE;
      
      const isWorkspaceOwner = profile.name.trim().toLowerCase() === WORKSPACE_OWNER.toLowerCase();

      let shouldNavigate = false;
      let targetPath = '';

      // if (isBusinessAnalyst) {
      //   // Business analyst can access their screens; redirect unknown paths to home
      //   if (!businessAnalystScreens.includes(currentPath)) {
      //     shouldNavigate = true;
      //     targetPath = '/';
      //   }
      // } else {
      if (isWorkspaceOwner) {
        // Workspace owner default landing is /aiAgents; redirect from chat, home, or any invalid path
        if (currentPath === '/chat' || currentPath === '/') {
          shouldNavigate = true;
          targetPath = '/aiAgents';
        } else if (!workspaceOwnerScreens.includes(currentPath)) {
          shouldNavigate = true;
          targetPath = '/aiAgents';
        }
      } else {
        // Any non-workspace-owner role (business analyst or otherwise) gets business analyst behavior
        if (!businessAnalystScreens.includes(currentPath)) {
          shouldNavigate = true;
          targetPath = '/';
        }
      }
      
      if (shouldNavigate && targetPath && currentPath !== targetPath) {
        isNavigatingRef.current = true;
        lastProfileRef.current = currentProfile;
        lastPathRef.current = targetPath;
        
        // Ensure navigation state is reset to home before navigating
        dispatch(resetNavigationToHome() as any);
        
        navigate(targetPath, { replace: true });
        
        // Reset navigation flag after a short delay
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 100);
      } else {
        // Update refs even if no navigation needed
        lastProfileRef.current = currentProfile;
        lastPathRef.current = currentPath;
      }
    }
  }, [location.pathname, loginData.preferredProfile, loginData.profileData, navigate, dispatch, businessAnalystScreens, workspaceOwnerScreens]);
};